import { Translations } from './fr';

export const en: Translations = {
  // Navigation
  nav: {
    products: 'Products',
    faq: 'FAQ',
    login: 'Log in',
    contact: 'Get in touch',
  },

  // Hero Section
  hero: {
    title: 'End-to-end',
    titleHighlight: 'AI recruitment.',
    subtitleBefore: 'Accelerate your entire recruitment pipeline. ',
    subtitleHighlight: 'All at once,',
    subtitleAfter: ' or one step at a time.',
    cta: 'Get in touch',
    scrollHint: 'Scroll to explore',
  },

  // Narrative Chapter
  narrative: {
    slide1: 'Talentees transforms recruitment into an end-to-end pipeline — from sourcing to offer.',
    slide2: 'Great hiring is a series of consistent decisions:\nsame criteria, same evidence, from start to finish.',
    slide3: 'Start today with screening.\nTomorrow, activate each agent step by step.',
  },

  // Capabilities Section (Recruitment Value Chain)
  capabilities: {
    title: 'The Recruitment Value Chain',
    subtitle: 'Talentees intelligently automates the key steps of your recruitment process.',
    steps: {
      needDefinition: {
        step: 'Need Definition',
        description: 'Position and criteria identification',
      },
      sourcing: {
        step: 'Sourcing',
        description: 'Candidate search and outreach',
        benefit: 'Automatic identification of the best profiles across all channels',
      },
      screening: {
        step: 'CV Screening',
        description: 'Application analysis and sorting',
        benefit: 'Intelligent analysis: score, signals, risks, and personalized questions',
      },
      hrInterview: {
        step: 'HR Interview',
        description: 'First exchange with candidates',
        benefit: 'Autonomous interview or real-time assistance to structure the evaluation',
      },
      technicalTest: {
        step: 'Technical Test',
        description: 'Practical skills assessment',
        benefit: 'Automatic generation and evaluation of adapted practical cases',
      },
      managerInterview: {
        step: 'Manager Interview',
        description: 'Validation by the manager',
        benefit: 'Intelligent assistant to guide and structure the interview',
      },
      proposal: {
        step: 'Proposal & Negotiation',
        description: 'Final offer and closing',
      },
    },
    legend: {
      automated: 'Automated by Talentees',
      automatedDesc: 'Specialized AI for each step',
      manual: 'Manual Process',
      manualDesc: 'Managed by your team',
    },
    stats: {
      automatedSteps: 'Automated steps',
      timeReduction: 'Processing time reduction',
      objectivity: 'Evaluation objectivity',
    },
  },

  // Agents Section
  agents: {
    title: 'Our AI Agents',
    subtitle: 'Each recruitment step orchestrated by a specialized agent.\nFrom sourcing to offer, an end-to-end pipeline.',
    available: 'Available now',
    comingSoon: 'Coming soon',
    learnMore: 'Learn more',
    keyFeatures: 'Key Features',
    features: [
      'Intelligent automation',
      'Deep contextual analysis',
      'Structured and actionable reports',
    ],
  },

  // Agent Names and Descriptions
  agentDetails: {
    victor: {
      name: 'Victor',
      step: 'CV Analysis (Screening)',
      description: 'Transforms a CV into a decision: multi-dimensional score, key signals, identified risks, and personalized questions.',
    },
    nora: {
      name: 'Nora',
      step: 'Sourcing',
      description: 'Identifies and approaches the best profiles on the right channels with surgical precision.',
    },
    clara: {
      name: 'Clara',
      step: 'Autonomous Interview',
      description: 'Conducts the interview end-to-end with contextual intelligence and produces a structured, comparable report.',
    },
    milo: {
      name: 'Milo',
      step: 'Interview Assistant',
      description: 'Assists the recruiter in real-time: guides the conversation, notes key points, and structures the evaluation.',
    },
    iris: {
      name: 'Iris',
      step: 'Case Study',
      description: 'Generates and evaluates practical cases adapted to the position with clear feedback and objective criteria.',
    },
  },

  // Pipeline Section
  pipeline: {
    label: 'The workflow',
    title: 'AI that works with you,',
    titleMuted: 'not in your place.',
    subtitle: 'You keep control and the decisions. AI handles the analysis, structuring, and repetitive tasks.',
    available: 'Available',
    comingSoon: 'Soon',
    steps: {
      jobs: {
        label: 'Feed with Jobs',
      },
      sourcing: {
        label: 'Sourcing',
        you: 'You define the role and criteria.',
        ai: 'AI identifies and reaches out to the best profiles across all channels.',
      },
      screening: {
        label: 'Screening',
        you: 'You make the final decision.',
        ai: 'AI analyses every CV — score, key signals, risks, recommended questions.',
      },
      interview: {
        label: 'Interview Copilot',
        you: 'You lead the interview and evaluate.',
        ai: 'AI guides you in real time, structures your notes, and produces the report.',
      },
      testcase: {
        label: 'Test Case',
        you: 'You validate the case and assess the candidate.',
        ai: 'AI generates a role-specific practical case with objective criteria.',
      },
    },
  },

  // FAQ Section
  faq: {
    title: 'FAQ.',
    questions: [
      {
        question: 'How does it work?',
        answer: 'Upload a CV and a job description. In under 30 seconds, Talentees generates a full report: multi-dimensional score, profile summary, key signals, identified risks, and personalized interview questions. You keep the final decision.',
      },
      {
        question: 'What data is stored?',
        answer: 'Only CVs and generated reports are retained. Default retention is 90 days, with deletion available on request at any time. Talentees is GDPR-compliant and no data is used to train third-party models.',
      },
      {
        question: 'How do you ensure transparency?',
        answer: "Every score is backed by direct quotes from the CV. You see exactly why a profile is rated the way it is — no black box. A complete audit trail is available for every evaluation.",
      },
      {
        question: 'Product roadmap?',
        answer: 'Sourcing and Screening are available right now. Interview Copilot and Test Case are being rolled out — activatable progressively based on your needs, as a full pipeline or pay as you go.',
      },
    ],
  },

  // Final CTA Section
  finalCta: {
    title: 'Ready to transform your recruitment?',
    subtitle: 'Join companies that hire faster and better with AI.',
    cta: 'Get in touch',
  },

  // Footer
  footer: {
    description: 'End-to-end AI recruitment. Accelerate your hiring with artificial intelligence.',
    products: 'Products',
    company: 'Company',
    companyLinks: {
      about: 'About',
      careers: 'Careers',
    },
    support: 'Support',
    supportLinks: {
      documentation: 'Documentation',
    },
    legal: 'Legal',
    legalLinks: {
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
    },
    copyright: 'Talentees. All rights reserved.',
  },

  // Contact Modal
  contact: {
    step1: {
      title: 'Your details',
      subtitle: "Let's get to know each other",
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Professional email',
    },
    step2: {
      title: 'What are your needs?',
      subtitle: 'Select one or more services',
      options: {
        screening: 'CV Screening',
        sourcing: 'Candidate Sourcing',
        interview: 'AI Interviews',
        caseStudies: 'Case Studies / Technical Tests',
      },
    },
    step3: {
      title: 'Your company',
      subtitle: 'Tell us about your company',
      companyName: 'Company name',
      companyDescription: 'Describe your business and recruitment challenges',
    },
    navigation: {
      step: 'Step',
      of: 'of',
      back: 'Back',
      continue: 'Continue',
      send: 'Send',
      sending: 'Sending...',
      pressEnter: 'Press Enter to continue',
    },
    success: 'Thank you! We will contact you very soon.',
    error: 'An error occurred. Please try again.',
  },

  // Legal Pages
  legal: {
    hub: {
      title: 'Legal Hub',
      subtitle: 'Find all our legal information and privacy policies.',
    },
    privacy: {
      title: 'Privacy Policy',
    },
    terms: {
      title: 'Terms of Service',
    },
    cookies: {
      title: 'Cookie Policy',
    },
    dpa: {
      title: 'Data Processing Agreement',
    },
    candidatesPrivacy: {
      title: 'Candidate Privacy',
    },
    backToHub: 'Back to legal hub',
    backToHome: 'Back to home',
  },

  // About Page
  about: {
    label: 'About',
    title: 'We believe the best',
    titleMuted: 'hiring is human.',
    intro: "Talentees was built on a simple observation: recruiters spend too much time on low-value tasks, and not enough on what truly matters — understanding a candidate, feeling a fit, making a good decision. Our mission is to give them that time back, with AI that works alongside them, not instead of them.",
    valuesLabel: 'Our values',
    values: [
      {
        label: 'The recruiter stays in control',
        description: "AI isn't here to replace human judgment — it's here to amplify it. Every decision stays in the hands of the recruiter.",
      },
      {
        label: 'Full transparency',
        description: "No black box. Every score, signal, and recommendation is backed by concrete evidence from the candidate's file.",
      },
      {
        label: 'Efficiency without compromise',
        description: "Reducing time spent on repetitive tasks doesn't mean lowering standards. We accelerate the process without sacrificing the quality of hires.",
      },
      {
        label: 'Modularity',
        description: "Every team is different. Talentees adapts to your workflow — full pipeline or step by step, based on your needs.",
      },
    ],
    ctaLabel: 'A question? An idea?',
    backHome: '← Back to home',
  },

  // Careers Page
  careers: {
    label: 'Careers',
    title: "We're building something",
    titleMuted: 'ambitious.',
    description: "No open positions right now — but if you think you can contribute, we're always open to a great application.",
    cta: 'Unsolicited application',
    backHome: '← Back to home',
  },

  // Agents Showcase Page
  agentsShowcase: {
    title: 'Our AI Agents',
    subtitle: 'Each recruitment step orchestrated by a specialized agent.\nFrom sourcing to offer, an end-to-end pipeline.',
    navHint: 'Navigate between agents',
  },
};
